import type {
  BCBreedingFeatureProperties,
  BCRoostFeatureProperties,
  DBCAFeatureProperties,
  DBCAFaunaFeatureProperties,
} from "../types/referenceData"

export const FAUNA_CONSERVATION_COLORS: Record<string, string> = {
  CR: "#991B1B",
  EN: "#DC2626",
  VU: "#EA580C",
  P1: "#C2410C",
  P2: "#CA8A04",
  P3: "#16A34A",
  P4: "#0D9488",
  P5: "#0891B2",
  MI: "#2563EB",
  OS: "#7C3AED",
  CD: "#9333EA",
}
const FAUNA_STATUS_ORDER = Object.keys(FAUNA_CONSERVATION_COLORS)
/** Combined codes retain their display value; colour by the most severe code. */
export function faunaConservationCode(value?: string): string | undefined {
  const codes = value?.toUpperCase().split(/\s*&\s*/)
  return FAUNA_STATUS_ORDER.find((code) => codes?.includes(code))
}
export function isFaunaProperties(
  properties: DBCAFeatureProperties,
): properties is DBCAFaunaFeatureProperties {
  return ["tpfa", "bc_roost", "bc_breeding"].includes(properties.sourceFormat)
}

/**
 * Survey-site records (black cockatoo roosts and breeding sites) have no
 * species; their `taxon` is a site label. Leave them out of species lists.
 */
export function isDBCASiteRecord(
  properties: DBCAFeatureProperties,
): properties is BCRoostFeatureProperties | BCBreedingFeatureProperties {
  return (
    properties.sourceFormat === "bc_roost" ||
    properties.sourceFormat === "bc_breeding"
  )
}

export const DEFAULT_FAUNA_MAX_ACCURACY_M = 1000
export function faunaAccuracyVisible(
  properties: DBCAFeatureProperties,
  maxAccuracyM: number | null,
  includeUnknown = true,
): boolean {
  if (properties.sourceFormat !== "tpfa") return true
  const accuracy = properties.accuracyM
  if (!accuracy || accuracy <= 0) return includeUnknown
  return maxAccuracyM === null || accuracy <= maxAccuracyM
}

export function faunaAccuracyLabel(
  properties: DBCAFaunaFeatureProperties,
): string {
  if (!properties.accuracyM) return "Unknown — location precision not recorded"
  const distance =
    properties.accuracyM >= 1000
      ? `${properties.accuracyM / 1000} km`
      : `${properties.accuracyM} m`
  return properties.accuracyM >= 18000
    ? `${distance} — grid-cell centroid, not an observation location`
    : `${distance} radius`
}

export function faunaDetailFields(
  properties: DBCAFaunaFeatureProperties,
): { label: string; value: string }[] {
  const rows: [string, string | number | null | undefined][] = [
    ["Location accuracy", faunaAccuracyLabel(properties)],
  ]
  if (properties.sourceFormat === "tpfa") {
    rows.push(
      ["Common name", properties.commonName],
      ["Class", properties.class],
      ["WA status (delivered)", properties.waStatus],
      ["EPBC status (delivered)", properties.epbcStatus],
      ["WA listing", properties.status],
      ["Observation date", properties.observationDate ?? "Unknown"],
      ["Date precision", properties.datePrecision],
      ["Source", properties.source],
      ["Source ID", properties.sourceId],
      ["Certainty", properties.certainty],
      ["Observation method", properties.obsMethod],
      ["Observation type", properties.obsType],
      ["Secondary sign", properties.secondarySign],
      ["Individuals", properties.count ? properties.count : "Not recorded"],
      ["Site", properties.site],
      ["DBCA fauna name ID", properties.dbcaNameId],
    )
  } else if (properties.sourceFormat === "bc_roost") {
    rows.push(
      ["Site code", properties.siteCode],
      ["Surveys", properties.surveys],
      [
        "White-tailed total / maximum",
        `${properties.whiteTailedTotal ?? "Unknown"} / ${properties.whiteTailedMax ?? "Unknown"}`,
      ],
      [
        "Forest red-tailed total / maximum",
        `${properties.forestRedTailedTotal ?? "Unknown"} / ${properties.forestRedTailedMax ?? "Unknown"}`,
      ],
    )
  } else {
    rows.push(
      ["Site ID", properties.siteId],
      ["Hollow type", properties.hollowType],
      ["Tree category", properties.treeCategory],
      [
        "First breeding year",
        properties.firstBreedingYear ?? "No breeding recorded",
      ],
      [
        "Last breeding year",
        properties.lastBreedingYear ?? "No breeding recorded",
      ],
      [properties.identifierField || "Identifier", properties.identifierValue],
    )
  }
  return rows.flatMap(([label, value]) =>
    value == null || value === "" ? [] : [{ label, value: String(value) }],
  )
}
